import type { Product, ProductVariant } from '@/lib/products';

const RAW_SITE_URL = process.env.NEXT_PUBLIC_SITE_URL?.trim();
export const SITE_URL = (RAW_SITE_URL && RAW_SITE_URL.length > 0 ? RAW_SITE_URL : 'https://klonkaffe.dk').replace(
  /\/+$/,
  ''
);

export const SITE_NAME = 'KlonKaffe';

/** Build an absolute URL from a site-relative path (or pass through an absolute URL). */
export function absoluteUrl(pathOrUrl?: string | null): string {
  const value = pathOrUrl?.trim();
  if (!value) return SITE_URL;
  if (/^https?:\/\//i.test(value)) return value;
  // encodeURI keeps already-encoded sequences intact while escaping spaces and
  // other unsafe characters that appear in media file paths.
  return encodeURI(`${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`);
}

/** Collect candidate product images as absolute URLs, best first, de-duplicated. */
export function productImageUrls(product: Product): string[] {
  const urls: string[] = [];
  const push = (src?: string | null) => {
    const value = src?.trim();
    if (value) urls.push(absoluteUrl(value));
  };

  push(product.cardImage);
  push(product.image);
  product.media?.forEach((m) => {
    if (m.type === 'image') push(m.src);
    push(m.poster);
  });

  return Array.from(new Set(urls));
}

/** Lowest and highest variant prices in major currency units (DKK). */
export function variantPriceRange(variants: ProductVariant[]): { low: number; high: number } {
  const prices = variants.map((v) => v.price / 100).filter((n) => Number.isFinite(n) && n > 0);
  if (prices.length === 0) return { low: 0, high: 0 };
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

/** schema.org/Product JSON-LD for Google product rich results. */
export function buildProductJsonLd(product: Product): Record<string, unknown> {
  const url = absoluteUrl(`/shop/${product.slug}`);
  const images = productImageUrls(product);
  const { low, high } = variantPriceRange(product.variants);

  const offers =
    low === high
      ? {
          '@type': 'Offer',
          price: low.toFixed(2),
          priceCurrency: 'DKK',
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url,
        }
      : {
          '@type': 'AggregateOffer',
          lowPrice: low.toFixed(2),
          highPrice: high.toFixed(2),
          offerCount: product.variants.length,
          priceCurrency: 'DKK',
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url,
        };

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    ...(images.length > 0 ? { image: images } : {}),
    sku: product.slug,
    productID: product.id,
    category: 'Kaffe',
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    ...(product.origin ? { countryOfOrigin: product.origin } : {}),
    offers,
  };
}

/** schema.org/BreadcrumbList JSON-LD (Forside → Shop → product). */
export function buildBreadcrumbJsonLd(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
